// BarChart (Components, BL-189): periods side by side, each a pair of bars (this period in ink, the one
// it's compared with muted), against one scale of four round steps. The period under way can split its
// comparison: solid to the same day, hatched for the rest of it. Periods in a band (the dates picked) sit
// on the grid's wash, and the latest period's figures stand at the right. A legend names what each bar is.
// On a phone (or with layout="rows") it becomes rows, newest first: the period, the two bars across, the
// figures. Its bars draw with Meter's fill and hatch (--bars-* read {meter-pen} and {hatch}). Each period
// says itself in words, on pointing, on focus and to a screen reader. Reads only --bars-*.
import './BarChart.css';

export interface BarPeriod {
  id: string;
  /** Its short name under the bars ("Oct"). */
  label: string;
  /** The group it's in ("2026"), under its first period and wherever it changes. */
  group?: string;
  value: number;
  /** What it's compared with (the same period a year before). */
  compare: number;
  /** The period under way: how much of `compare` had come by the same day; the rest is hatched. */
  compareSoFar?: number;
  /** In the band (the dates picked). */
  inBand?: boolean;
  /** The period in words, for pointing, focus and a screen reader. */
  words: string;
  /** Rows: the value's figure and the compared figure, in words ("$113,800 so far", "$128,940 by 27 Oct"). */
  valueText: string;
  compareText: string;
}

export interface BarChartProps {
  periods: readonly BarPeriod[];
  /** What it shows, in a sentence, for a screen reader. */
  label: string;
  /** A tick's words ($50k). */
  format: (v: number) => string;
  /** What each bar is: the value, the compared one, the hatched rest and the band, when there are some. */
  legend: { value: string; compare: string; rest?: string; band?: string };
  /** The latest period's figures at the right of the chart. */
  end?: { main: string; sub: string };
  /** chart: bars over the periods (rows on a phone); rows: always rows (a narrow column). */
  layout?: 'chart' | 'rows';
}

/** A round top for the scale and its step, four steps high. */
export function niceScale(max: number): { step: number; top: number } {
  const raw = Math.max(1, max) / 4;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((k) => k * mag).find((s) => s >= raw) ?? mag * 10;
  return { step, top: step * 4 };
}

const share = (v: number, top: number) => `${Math.max(0, Math.min(100, (v / (top || 1)) * 100)).toFixed(1)}%`;

/** Each bar as a share of the scale: the chart's scale is round, the rows' is the largest figure. */
export function barShapes(periods: readonly BarPeriod[]) {
  const max = Math.max(1, ...periods.flatMap((p) => [p.value, p.compare]));
  const { step, top } = niceScale(max);
  const shapes = (t: number) =>
    periods.map((p, i) => ({
      p,
      value: share(p.value, t),
      compare: share(p.compare, t),
      /** The solid part of the compared bar, as a share of that bar; null when it isn't split. */
      soFar: p.compareSoFar != null && p.compare > p.compareSoFar ? share(p.compareSoFar, p.compare) : null,
      showGroup: !!p.group && (i === 0 || periods[i - 1]!.group !== p.group),
    }));
  return {
    ticks: [0, 1, 2, 3, 4].map((k) => ({ at: share(k * step, top), value: k * step })),
    chart: shapes(top),
    rows: shapes(max).reverse(),
  };
}

function Legend({ legend, rest, band }: { legend: BarChartProps['legend']; rest: boolean; band: boolean }) {
  return (
    <p className="fl-bars__legend">
      <span>
        <i className="fl-bars__key" data-bar="value" />
        {legend.value}
      </span>
      <span>
        <i className="fl-bars__key" data-bar="compare" />
        {legend.compare}
      </span>
      {rest && legend.rest && (
        <span>
          <i className="fl-bars__key" data-bar="rest" />
          {legend.rest}
        </span>
      )}
      {band && legend.band && (
        <span>
          <i className="fl-bars__key" data-bar="band" />
          {legend.band}
        </span>
      )}
    </p>
  );
}

/** A bar's length: up the chart, or across a row. */
const along = (axis: Axis, size: string) => (axis === 'up' ? { blockSize: size } : { inlineSize: size });
type Axis = 'up' | 'across';

/** The compared bar: solid, or solid to the same day with the rest hatched above (or after) it. */
const Compare = ({ size, soFar, axis }: { size: string; soFar: string | null; axis: Axis }) => (
  <i
    className="fl-bars__bar"
    data-bar="compare"
    data-split={soFar ? '' : undefined}
    style={along(axis, size)}
  >
    {soFar && <i className="fl-bars__solid" style={along(axis, soFar)} />}
  </i>
);

export function BarChart({ periods, label, format, legend, end, layout = 'chart' }: BarChartProps) {
  const s = barShapes(periods);
  const rest = s.chart.some((x) => x.soFar);
  const band = periods.some((p) => p.inBand);
  return (
    <figure className="fl-bars" data-layout={layout} aria-label={label}>
      <Legend legend={legend} rest={rest} band={band} />
      {layout === 'chart' && (
        <div className="fl-bars__plot">
          <div className="fl-bars__scale" aria-hidden="true">
            {s.ticks.map((t) => (
              <span key={t.value} className="fl-bars__tick" style={{ insetBlockEnd: t.at }}>
                <span className="fl-bars__tickword">{format(t.value)}</span>
              </span>
            ))}
          </div>
          <ol className="fl-bars__periods">
            {s.chart.map((x) => (
              <li
                key={x.p.id}
                className="fl-bars__period"
                data-band={x.p.inBand ? '' : undefined}
                tabIndex={0}
                aria-label={x.p.words}
                title={x.p.words}
              >
                <span className="fl-bars__pair" aria-hidden="true">
                  <Compare size={x.compare} soFar={x.soFar} axis="up" />
                  <i className="fl-bars__bar" data-bar="value" style={along('up', x.value)} />
                </span>
                <span className="fl-bars__x" aria-hidden="true">
                  {x.p.label}
                </span>
                <span className="fl-bars__group" aria-hidden="true">
                  {x.showGroup ? x.p.group : ''}
                </span>
              </li>
            ))}
          </ol>
          {end && (
            <p className="fl-bars__end" aria-hidden="true">
              <b>{end.main}</b>
              <span>{end.sub}</span>
            </p>
          )}
        </div>
      )}
      <ol className="fl-bars__rows">
        {s.rows.map((x) => (
          <li
            key={x.p.id}
            className="fl-bars__row"
            data-band={x.p.inBand ? '' : undefined}
            aria-label={x.p.words}
          >
            <span className="fl-bars__rname" aria-hidden="true">
              {x.p.label}
              {x.p.group && <small>{x.p.group}</small>}
            </span>
            <span className="fl-bars__rbars" aria-hidden="true">
              <i className="fl-bars__bar" data-bar="value" style={along('across', x.value)} />
              <Compare size={x.compare} soFar={x.soFar} axis="across" />
            </span>
            <span className="fl-bars__rvalue" aria-hidden="true">
              {x.p.valueText}
              <small>{x.p.compareText}</small>
            </span>
          </li>
        ))}
      </ol>
    </figure>
  );
}
